/**
 * @file Detail long-text display, edit, and overflow behavior.
 */

import {
    bindNativeFieldBehavior,
} from '../forms/nativeFieldStyles.js';
import {
    extractPlainTextFromHTML,
    formatAndSanitizeUserContent,
} from '../utils/contentUtils.js';

const LONG_TEXT_DISPLAY_CLASSES = [
    'long-text-display min-h-[128px] max-h-[200px] overflow-y-auto rounded-md',
    'border border-gray-200 bg-white p-2 text-base text-gray-700',
    'transition-all duration-200 hover:border-[var(--dfc-brand-primary)]',
    'focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]',
    'sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
].join(' ');

const LONG_TEXT_EDITOR_CLASSES = [
    'long-text-editor native-backed-field min-h-[128px] max-h-[200px] w-full',
    'resize-none overflow-y-auto rounded-md border border-gray-200 bg-white p-2',
    'text-base text-gray-700 placeholder:text-gray-400',
    'hover:border-[var(--dfc-brand-primary)] focus:outline-none',
    'focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)] sm:text-sm',
    'dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200',
    'dark:placeholder-gray-400',
].join(' ');

/**
 * Returns the hidden native value field for a long-text detail container.
 * @param {HTMLElement} container Long-text field container.
 * @returns {HTMLTextAreaElement|null} Hidden value field.
 */
export function getLongTextValueField(container) {
    return container?.querySelector?.('[data-long-text-value]') || null;
}

/**
 * Returns the schema field used for the parent detail long-text body.
 * @param {string} itemType Detail item type.
 * @returns {string} Field name.
 */
export function getLongTextFieldName(itemType) {
    if (itemType === 'task') {return 'description';}
    if (itemType === 'maintenance' || itemType === 'lab') {return 'details';}
    return 'comments';
}

/**
 * Finds a long-text container by field name.
 * @param {HTMLElement} detailsViewContainer Detail view container.
 * @param {string} fieldName Schema field name.
 * @returns {HTMLElement|null} Long-text container.
 */
export function getLongTextContainer(detailsViewContainer, fieldName) {
    return detailsViewContainer?.querySelector?.(
        `.long-text-field-container[data-field-name="${fieldName}"]`,
    ) || null;
}

function getLongTextLabel(container) {
    return container?.querySelector?.('span')?.textContent?.trim() || 'Details';
}

function createLongTextDisplay(value, label) {
    const display = document.createElement('div');
    display.className = LONG_TEXT_DISPLAY_CLASSES;
    display.setAttribute('role', 'textbox');
    display.setAttribute('tabindex', '0');
    display.setAttribute('aria-label', label);
    display.innerHTML = formatAndSanitizeUserContent(value);
    return display;
}

/**
 * Updates the capped long-text overflow indicator state.
 * @param {HTMLElement} container Long-text field container.
 */
export function updateLongTextOverflow(container) {
    const display = container?.querySelector?.('.long-text-display');
    const indicator = container?.querySelector?.('.overflow-indicator');

    if (!display || !indicator || indicator.classList.contains('hidden')) {
        return;
    }

    const isOverflowing = display.scrollHeight > display.clientHeight;
    const isScrolledToBottom = Math.abs(
        display.scrollHeight - display.scrollTop - display.clientHeight,
    ) < 10;

    indicator.classList.toggle('opacity-0', !isOverflowing || isScrolledToBottom);
}

function commitLongTextEditor(container, enableSaveButtons) {
    const editor = container.querySelector('.long-text-editor');
    const valueField = getLongTextValueField(container);
    if (!editor || !valueField) {return;}

    valueField.value = editor.value;
    editor.replaceWith(createLongTextDisplay(editor.value, getLongTextLabel(container)));

    const indicator = container.querySelector('.overflow-indicator');
    if (indicator) {
        indicator.classList.remove('hidden');
    }
    bindLongTextDisplay(container, enableSaveButtons);
    requestAnimationFrame(() => updateLongTextOverflow(container));
}

function beginLongTextEdit(container, enableSaveButtons) {
    if (container.querySelector('.long-text-editor')) {return;}

    const fieldName = container.dataset.fieldName;
    const display = container.querySelector('.long-text-display');
    const valueField = getLongTextValueField(container);
    if (!fieldName || !display || !valueField) {return;}

    const editor = document.createElement('textarea');
    editor.className = LONG_TEXT_EDITOR_CLASSES;
    editor.value = extractPlainTextFromHTML(valueField.value || '');
    editor.style.height = `${display.offsetHeight}px`;

    const indicator = container.querySelector('.overflow-indicator');
    if (indicator) {
        indicator.classList.add('hidden', 'opacity-0');
    }

    display.replaceWith(editor);
    bindNativeFieldBehavior(container);
    editor.focus();

    editor.addEventListener('input', () => {
        valueField.value = editor.value;
        enableSaveButtons(fieldName);
    });
    requestAnimationFrame(() => {
        editor.addEventListener('blur', () => {
            commitLongTextEditor(container, enableSaveButtons);
        });
    });
}

/**
 * Binds click/focus editing and overflow behavior for a long-text display.
 * @param {HTMLElement} container Long-text field container.
 * @param {Function} [enableSaveButtons] Dirty-state callback.
 */
export function bindLongTextDisplay(container, enableSaveButtons = () => {}) {
    const display = container?.querySelector?.('.long-text-display');
    if (!display || display.dataset.longTextBound === 'true') {return;}

    display.dataset.longTextBound = 'true';
    display.addEventListener('click', (event) => {
        if (event.target.closest('a')) {return;}
        beginLongTextEdit(container, enableSaveButtons);
    });
    display.addEventListener('focus', () => {
        beginLongTextEdit(container, enableSaveButtons);
    });
    display.addEventListener('scroll', () => updateLongTextOverflow(container));
}

/**
 * Syncs long-text displays from hidden native value fields after draft restore.
 * @param {HTMLElement} root Detail view root.
 */
export function syncLongTextDisplaysFromForm(root) {
    root?.querySelectorAll?.('.long-text-field-container').forEach(container => {
        const valueField = getLongTextValueField(container);
        const display = container.querySelector('.long-text-display');
        if (!valueField || !display) {return;}
        display.innerHTML = formatAndSanitizeUserContent(valueField.value);
    });
}

/**
 * Sets a long-text container value and refreshes display/editor state.
 * @param {HTMLElement|null} container Long-text field container.
 * @param {string} value New value.
 * @param {Function} enableSaveButtons Dirty-state callback.
 */
export function setLongTextContainerValue(container, value, enableSaveButtons) {
    if (!container) {return;}

    const valueField = getLongTextValueField(container);
    if (valueField) {
        valueField.value = value;
    }

    const editor = container.querySelector('.long-text-editor');
    if (editor) {
        editor.replaceWith(createLongTextDisplay(value, getLongTextLabel(container)));
    }

    const display = container.querySelector('.long-text-display');
    if (display) {
        display.innerHTML = formatAndSanitizeUserContent(value);
    }

    bindLongTextDisplay(container, enableSaveButtons);
    requestAnimationFrame(() => updateLongTextOverflow(container));
}
